import {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from "react";
import SnackbarComponent from "../components/SnackbarComponent";
import { AlertColor } from "@mui/material/Alert/Alert";
import BackdropComponent from "../components/BackdropComponent";
import { getPermissions, getToken, getUserId, hasAllPermission, isSportsPlan } from "services/DefaultService";
import { Permissions } from "services/enums";
import { AnyOfPermissions } from "routes/types";
import {
	applyThemeVars,
	AppThemeName,
	getStoredTheme,
} from "styles/global.styles";
import {
	getConfigByIdAsync,
	updateConfigAsync,
} from "services/Configs";
import { SipClientStatus } from "../services/SipClientService/enums";
import { getStreamByEndpointAsync } from "../services/Events";
import { ENDPOINT_SIP_CLIENT } from "../services/SipClientService";
import CallRenderModal from "../pages/RemoteInspector/_components/CallRenderModal";
import {
	ISipClientHistoryProps,
	ISipClientProps,
	ISipClientStatusDetails,
	ISipClientStatusDetailsProps,
} from "../services/SipClientService/types";
import { useGetLastEndedCallByLoggedUser } from "../services/SipClientService/hooks/useMutation";

interface AppContextProviderProps {
	children: ReactNode;
}

export const AppContext = createContext<AppContextData>({} as AppContextData);

interface AppContextData {
	showSnackbar: (message: string, severity: AlertColor) => void;
	requestNotificationPermission: () => Promise<boolean>;
	sendBrowserNotification: (title: string, body?: string) => void;
	isOpenedSidebar: boolean;
	setIsOpenedSidebar: Dispatch<SetStateAction<boolean>>;
	isShowLoadingComponent: boolean;
	isRouteAllowed: (permission?: Permissions | Permissions[] | AnyOfPermissions) => boolean;
	setIsShowLoadingComponent: Dispatch<SetStateAction<boolean>>;
	formData: any;
	audioRef: any;
	incomingRef: any;
	setFormData: Dispatch<SetStateAction<any>>;
	sipClientStatusDetails: ISipClientStatusDetailsProps;
	sipClientRegister?: ISipClientProps;
	renderTitle: () => string;
	isOpenFillingReasonModal: boolean;
	setIsOpenFillingReasonModal: Dispatch<SetStateAction<boolean>>;
	lastSipClientHistory: ISipClientHistoryProps | undefined;
	setLastSipClientHistory: Dispatch<
		SetStateAction<ISipClientHistoryProps | undefined>
	>;
	theme: AppThemeName;
	setTheme: (theme: AppThemeName) => void;
}

const AppContextProvider = ({ children }: AppContextProviderProps) => {
	const [isShowLoadingComponent, setIsShowLoadingComponent] =
		useState<boolean>(false);
	const [sipClientStatusDetails, setSipClientStatusDetails] =
		useState<ISipClientStatusDetailsProps>({
			status: SipClientStatus.NO_ACTIVITY,
			callInfos: [],
		});
	const [sipClientRegister, setSipClientRegister] = useState<
		ISipClientProps | undefined
	>();
	const [isOpenFillingReasonModal, setIsOpenFillingReasonModal] =
		useState(false);
	const audioRef = useRef<any>(null);
	const incomingRef = useRef<any>(null);
	const callingRef = useRef<any>(null);
	const prevIsOnCall = useRef(false);
	const [isOnCall, setIsOnCall] = useState(false);
	const [lastSipClientHistory, setLastSipClientHistory] = useState<
		ISipClientHistoryProps | undefined
	>();

	const pathname = window.location.pathname;
	const [isOpenedSidebar, setIsOpenedSidebar] = useState(false);
	const [isOpenSnackbar, setIsOpenSnackbar] = useState<boolean>(false);
	const [snackbarSeverity, setSnackbarSeverity] =
		useState<AlertColor>("success");
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [formData, setFormData] = useState<any>({});
	const [theme, setThemeState] = useState<AppThemeName>(getStoredTheme());

	const { mutateAsync: mutateGetLastEndedCallByLoggedUser } =
		useGetLastEndedCallByLoggedUser();

	// setTheme aplica na hora (CSS vars + localStorage) e só depois tenta persistir na licença —
	// se a chamada falhar, a pessoa ainda vê o tema trocado (não trava numa preferência que não
	// "pegou" visualmente), só não fica salvo pra outros dispositivos/sessões.
	const setTheme = (newTheme: AppThemeName) => {
		setThemeState(newTheme);
		applyThemeVars(newTheme);
		updateConfigAsync({ data: { theme: newTheme } }).catch(() => {
			showSnackbar(
				"Tema aplicado, mas não foi possível salvar a preferência.",
				"warning",
			);
		});
	};

	// Ao carregar, busca a preferência real salva na licença (o valor do localStorage já foi
	// aplicado de forma síncrona no import de global.styles.ts, então não há flash) e reconcilia
	// caso divirja (ex: mesma conta acessada de outro dispositivo).
	useEffect(() => {
		if (!getToken()) return;

		getConfigByIdAsync()
			.then((config) => {
				const remoteTheme: AppThemeName =
					config?.theme === "BLACK_WHITE" ? "BLACK_WHITE" : "BLUE";
				setThemeState(remoteTheme);
				applyThemeVars(remoteTheme);
			})
			.catch(() => {
				// Best-effort: se a busca falhar, mantém o que já estava aplicado do localStorage.
			});
	}, []);

	const showSnackbar = (message: string, severity: AlertColor) => {
		setSnackbarMessage(message);
		setIsOpenSnackbar(true);
		setSnackbarSeverity(severity);
	};

	const isRouteAllowed = (permission?: Permissions | Permissions[] | AnyOfPermissions) => {
		if (hasAllPermission()) {
			return true;
		}

		if (permission && typeof permission === 'object' && 'any' in permission) {
			return permission.any.some((p) => getPermissions()?.includes(p));
		}

		// Plano Sports autoriza por si só as permissões SPORT_UNITIES*, independente do que
		// veio atribuído no token — evita esconder/bloquear a feature por permissão faltante.
		const isGrantedBySportsPlan = (p: Permissions) => isSportsPlan() && p.startsWith('SPORT_UNITIES');
		if (
			permission &&
			(Array.isArray(permission)
				? permission.every((p) => isGrantedBySportsPlan(p))
				: isGrantedBySportsPlan(permission))
		) {
			return true;
		}

		if (
			getPermissions() &&
			permission &&
			((Array.isArray(permission) &&
				permission.every((p) => getPermissions()?.includes(p))) ||
				(!Array.isArray(permission) && getPermissions()?.includes(permission)))
		) {
			return true;
		}

		return !permission;
	};

	const renderTitle = () => {
		if (sipClientStatusDetails.callInfos.length == 0) return "";

		switch (sipClientStatusDetails.status) {
			case SipClientStatus.RECEIVING_CALL:
			case SipClientStatus.MANY_CALL_WAITING:
				return (
					"Recebendo chamada de " +
					sipClientStatusDetails.callInfos[0].callFromName
				);
			case SipClientStatus.CALLING:
			case SipClientStatus.CALL_WAITING_WHILE_CALLING:
				return "Chamando...";
			case SipClientStatus.ON_CALL:
			case SipClientStatus.CALL_WAITING:
				return (
					"Em chamada com " + sipClientStatusDetails.callInfos[0].callFromName
				);
			default:
				return "";
		}
	};

	useEffect(() => {
		getStreamByEndpointAsync(
			ENDPOINT_SIP_CLIENT + `/status/byLoggedUser`,
			setSipClientStatusDetails,
			undefined,
			undefined,
			undefined,
			undefined,
			false,
		);
	}, []);

	const isNotificationSupported =
		typeof window !== "undefined" && "Notification" in window;

	const requestNotificationPermission = async (): Promise<boolean> => {
		if (!isNotificationSupported) return false;
		if (window.Notification.permission === "granted") return true;
		if (window.Notification.permission === "denied") return false;

		try {
			const result = await window.Notification.requestPermission();
			return result === "granted";
		} catch {
			return false;
		}
	};

	const sendBrowserNotification = (title: string, body?: string) => {
		if (!isNotificationSupported) return;
		if (window.Notification.permission !== "granted") return;
		new window.Notification(title, { body });
	};

	useEffect(() => {
		const onCallNow =
			sipClientStatusDetails.status === SipClientStatus.ON_CALL ||
			sipClientStatusDetails.status === SipClientStatus.CALL_WAITING;

		if (!onCallNow && prevIsOnCall.current) {
			mutateGetLastEndedCallByLoggedUser().then(setLastSipClientHistory);
			setIsOpenFillingReasonModal(true);
		}

		prevIsOnCall.current = onCallNow;
	}, [sipClientStatusDetails.status]);

	useEffect(() => {
		if (
			sipClientStatusDetails.status == SipClientStatus.CALLING ||
			sipClientStatusDetails.status ==
				SipClientStatus.CALL_WAITING_WHILE_CALLING
		) {
			callingRef.current.play().catch((e: any) => {
				console.error("Erro ao tentar tocar a audio de ligação:", e);
			});
		} else {
			callingRef.current!.pause();
		}

		if (
			sipClientStatusDetails.status == SipClientStatus.RECEIVING_CALL ||
			sipClientStatusDetails.status == SipClientStatus.MANY_CALL_WAITING
		) {
			incomingRef.current.play().catch((e: any) => {
				console.error("Erro ao tentar tocar a audio de ligação:", e);
			});
		} else {
			incomingRef.current!.pause();
		}
	}, [sipClientStatusDetails.status]);

	useEffect(() => {
		if (sipClientStatusDetails.sipMessage) {
			showSnackbar(sipClientStatusDetails.sipMessage, "info");
		}
	}, [sipClientStatusDetails.status]);

	useEffect(() => {
		getStreamByEndpointAsync(
			ENDPOINT_SIP_CLIENT + `/${getUserId()}`,
			setSipClientRegister,
			undefined,
			undefined,
			undefined,
			undefined,
			false,
		);
	}, []);

	return (
		<AppContext.Provider
			value={{
				showSnackbar,
				requestNotificationPermission,
				sendBrowserNotification,
				isRouteAllowed,
				isOpenedSidebar,
				setIsOpenedSidebar,
				isShowLoadingComponent,
				setIsShowLoadingComponent,
				formData,
				setFormData,
				audioRef,
				incomingRef,
				sipClientStatusDetails,
				sipClientRegister,
				renderTitle,
				setIsOpenFillingReasonModal,
				isOpenFillingReasonModal,
				lastSipClientHistory,
				setLastSipClientHistory,
				theme,
				setTheme,
			}}
		>
			{children}
			<SnackbarComponent
				open={isOpenSnackbar}
				severity={snackbarSeverity}
				setOpen={setIsOpenSnackbar}
				message={snackbarMessage}
			/>

			<BackdropComponent
				open={isShowLoadingComponent}
				setOpen={setIsShowLoadingComponent}
			/>
			<audio
				ref={audioRef}
				src={"/siren.mp3"}
				loop
				tabIndex={-1}
				controls={false}
			/>

			<audio
				ref={incomingRef}
				src={"/phone_call.mp3"}
				loop
				tabIndex={-1}
				controls={false}
			/>

			<audio
				ref={callingRef}
				src={"/calling.mp3"}
				loop
				tabIndex={-1}
				controls={false}
			/>

			{sipClientStatusDetails.status != SipClientStatus.NO_ACTIVITY &&
				pathname !== "/app/remoteInspector" && <CallRenderModal />}
		</AppContext.Provider>
	);
};

export default AppContextProvider;
